import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { SOCIAL_PLATFORMS } from "@/lib/constants";
import BrandKit from "@/components/BrandKit";
import BrandAssets from "@/components/BrandAssets";

const TABS = ["Anagrafica", "Social", "Obiettivi", "Brand Kit", "Documenti", "Libreria"];

export default function BrandDetail() {
  const { brandId } = useParams();
  const [brand, setBrand] = useState(null);
  const [tab, setTab] = useState("Anagrafica");

  const load = useCallback(() => {
    api.get(`/brands/${brandId}`).then(({ data }) => setBrand(data)).catch(() => {});
  }, [brandId]);
  useEffect(() => { load(); }, [load]);

  if (!brand) return null;

  return (
    <div data-testid="brand-detail-page">
      <Link to="/brand" data-testid="back-to-brands" className="flex items-center gap-2 text-stone2e hover:text-ink transition-colors mb-4">
        <ArrowLeft strokeWidth={1.5} size={18} />
        <span className="font-display text-3xl text-ink">{brand.name}</span>
      </Link>
      <p className="meta-label mb-8">{(brand.settore || "").toUpperCase()}</p>

      <div className="flex gap-6 border-b border-black/10 mb-10 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`brand-tab-${t.toLowerCase().replace(" ", "-")}`}
            className={`pb-3 whitespace-nowrap font-body text-sm transition-colors border-b-2 -mb-px ${tab === t ? "border-black text-ink" : "border-transparent text-stone2e hover:text-ink"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Anagrafica" && <AnagraficaTab brand={brand} onSaved={load} />}
      {tab === "Social" && <SocialTab brand={brand} onSaved={load} />}
      {tab === "Obiettivi" && <ObjectivesTab brand={brand} onSaved={load} />}
      {tab === "Brand Kit" && <BrandKit brand={brand} onSaved={load} />}
      {tab === "Documenti" && <BrandAssets brandId={brand.id} kind="documento" />}
      {tab === "Libreria" && <BrandAssets brandId={brand.id} kind="libreria" withCategories />}
    </div>
  );
}

function AnagraficaTab({ brand, onSaved }) {
  const [f, setF] = useState({
    name: brand.name, referente: brand.referente, settore: brand.settore,
    note_contrattuali: brand.note_contrattuali, tone_of_voice: brand.tone_of_voice,
    guidelines: brand.guidelines,
  });

  const save = async () => {
    try {
      await api.patch(`/brands/${brand.id}`, f);
      toast.success("Anagrafica salvata");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const field = (key, label, textarea) => (
    <div>
      <p className="meta-label mb-2">{label}</p>
      {textarea ? (
        <textarea rows={4} value={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.value })} data-testid={`brand-field-${key}`} className="field-line resize-none" />
      ) : (
        <input value={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.value })} data-testid={`brand-field-${key}`} className="field-line" />
      )}
    </div>
  );

  return (
    <div className="max-w-2xl space-y-7" data-testid="anagrafica-tab">
      {field("name", "NOME BRAND")}
      {field("referente", "REFERENTE")}
      {field("settore", "SETTORE")}
      {field("note_contrattuali", "NOTE CONTRATTUALI", true)}
      {field("tone_of_voice", "TONO DI VOCE E LINEE GUIDA", true)}
      {field("guidelines", "OBIETTIVI GENERALI / ALTRE LINEE GUIDA", true)}
      <button onClick={save} data-testid="save-anagrafica-button" className="bg-black text-greige px-8 py-3 text-xs uppercase tracking-widest hover:bg-stone2e transition-colors">Salva</button>
    </div>
  );
}

function SocialTab({ brand, onSaved }) {
  const [rows, setRows] = useState(brand.social_accounts || []);
  const [nf, setNf] = useState({ platform: "Instagram", handle: "", url: "" });

  const save = async (next) => {
    try {
      await api.patch(`/brands/${brand.id}`, { social_accounts: next });
      setRows(next);
      onSaved();
      toast.success("Account social aggiornati");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-2xl" data-testid="social-tab">
      <p className="font-body text-sm text-stone2e mb-6">Account collegati a scopo di visualizzazione. L'integrazione reale arriverà in una fase successiva.</p>
      <div className="space-y-px bg-black/10 border border-black/10 mb-8">
        {rows.length === 0 && <p className="bg-greige/60 p-4 font-editorial italic text-stone2e">Nessun account social inserito.</p>}
        {rows.map((r, i) => (
          <div key={i} className="bg-greige/60 p-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-body text-sm text-ink">{r.platform} · <span className="font-medium">{r.handle}</span></p>
              {r.url && <p className="font-mono2e text-[0.65rem] text-stone2e truncate">{r.url}</p>}
            </div>
            <button onClick={() => save(rows.filter((_, j) => j !== i))} data-testid={`delete-social-${i}`} className="text-stone2e hover:text-ink"><Trash2 size={15} strokeWidth={1.5} /></button>
          </div>
        ))}
      </div>
      <div className="bg-white/70 border border-white p-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <select value={nf.platform} onChange={(e) => setNf({ ...nf, platform: e.target.value })} data-testid="social-platform-select" className="field-line bg-transparent">
            {SOCIAL_PLATFORMS.map((p) => <option key={p}>{p}</option>)}
          </select>
          <input value={nf.handle} onChange={(e) => setNf({ ...nf, handle: e.target.value })} placeholder="@handle" data-testid="social-handle-input" className="field-line" />
          <input value={nf.url} onChange={(e) => setNf({ ...nf, url: e.target.value })} placeholder="URL profilo" data-testid="social-url-input" className="field-line" />
        </div>
        <button onClick={() => { if (nf.handle.trim()) { save([...rows, nf]); setNf({ platform: "Instagram", handle: "", url: "" }); } }} data-testid="add-social-button" className="inline-flex items-center gap-2 bg-black text-greige px-5 py-2 text-xs uppercase tracking-widest">
          <Plus size={14} strokeWidth={1.5} /> Aggiungi
        </button>
      </div>
    </div>
  );
}

function ObjectivesTab({ brand, onSaved }) {
  const [rows, setRows] = useState(brand.objectives || []);
  const [nf, setNf] = useState({ period: "", text: "" });

  const save = async (next) => {
    try {
      await api.patch(`/brands/${brand.id}`, { objectives: next });
      setRows(next);
      onSaved();
      toast.success("Obiettivi aggiornati");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-2xl" data-testid="objectives-tab">
      <p className="font-body text-sm text-stone2e mb-6">Obiettivi misurabili per periodo. Vengono richiamati nei suggerimenti AI dei report.</p>
      <div className="space-y-px bg-black/10 border border-black/10 mb-8">
        {rows.length === 0 && <p className="bg-greige/60 p-4 font-editorial italic text-stone2e">Nessun obiettivo impostato.</p>}
        {rows.map((r, i) => (
          <div key={r.id || i} className="bg-greige/60 p-4 flex items-start justify-between gap-3">
            <div>
              <p className="meta-label mb-1">{(r.period || "").toUpperCase()}</p>
              <p className="font-body text-sm text-ink">{r.text}</p>
            </div>
            <button onClick={() => save(rows.filter((_, j) => j !== i))} data-testid={`delete-objective-${i}`} className="text-stone2e hover:text-ink shrink-0"><Trash2 size={15} strokeWidth={1.5} /></button>
          </div>
        ))}
      </div>
      <div className="bg-white/70 border border-white p-5">
        <input value={nf.period} onChange={(e) => setNf({ ...nf, period: e.target.value })} placeholder="Periodo (es. Q3 2026, Settembre)" data-testid="objective-period-input" className="field-line mb-3" />
        <textarea rows={2} value={nf.text} onChange={(e) => setNf({ ...nf, text: e.target.value })} placeholder="Obiettivo misurabile (es. +15% follower IG)" data-testid="objective-text-input" className="field-line resize-none mb-4" />
        <button onClick={() => { if (nf.text.trim()) { save([...rows, { id: crypto.randomUUID(), ...nf }]); setNf({ period: "", text: "" }); } }} data-testid="add-objective-button" className="inline-flex items-center gap-2 bg-black text-greige px-5 py-2 text-xs uppercase tracking-widest">
          <Plus size={14} strokeWidth={1.5} /> Aggiungi obiettivo
        </button>
      </div>
    </div>
  );
}
